import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControlLabel,
  Switch,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
  Chip,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  Alert,
  TextField,
  CircularProgress,
} from '@mui/material';
import {
  Settings,
  Brightness4,
  Brightness7,
  Info,
  Person,
  Email,
  ContentCopy,
  Refresh,
  Check,
  Close,
  Group,
  Badge,
  GroupAdd,
  Edit as EditIcon,
  Apple,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { closeDialog, setTheme } from '../../store/slices/uiSlice';
import { setOrganization, setMembers, setUser } from '../../store/slices/authSlice';
import { usePermissions } from '../../hooks/usePermissions';
import { MemberService } from '../../firebase/memberService';
import { AuthService } from '../../firebase/authService';
import { OrgMember, MemberRole } from '../../types/models';

const roleLabels: Record<MemberRole, string> = {
  viewer: 'Viewer',
  editor: 'Editor',
  admin: 'Admin',
};

const roleColors: Record<MemberRole, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  viewer: 'default',
  editor: 'info',
  admin: 'warning',
};

const SettingsDialog: React.FC = () => {
  const dispatch = useDispatch();
  const { dialog } = useSelector((state: RootState) => state.ui);
  const { user, organization, members } = useSelector((state: RootState) => state.auth);
  const { theme } = useSelector((state: RootState) => state.ui);
  const { canManageMembers } = usePermissions();

  const [copied, setCopied] = useState(false);
  const [approveRole, setApproveRole] = useState<Record<string, MemberRole>>({});
  const [joinCode, setJoinCode] = useState('');
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [joinSuccess, setJoinSuccess] = useState(false);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [nameSaving, setNameSaving] = useState(false);

  const isOpen = dialog.open && dialog.type === 'settings';

  // Sync name fields when dialog opens
  useEffect(() => {
    if (isOpen && user) {
      setEditFirstName(user.firstName || '');
      setEditLastName(user.lastName || '');
      setEditingName(false);
    }
  }, [isOpen, user]);

  const handleNameSave = async () => {
    if (!user) return;
    const first = editFirstName.trim();
    const last = editLastName.trim();
    if (first === (user.firstName || '') && last === (user.lastName || '')) {
      setEditingName(false);
      return;
    }
    setNameSaving(true);
    try {
      await AuthService.updateUserName(user.userID, first, last);
      dispatch(setUser({ ...user, firstName: first, lastName: last }));
    } catch (err) {
      console.error('Failed to update name:', err);
    } finally {
      setNameSaving(false);
      setEditingName(false);
    }
  };

  // Subscribe to members when dialog opens (admin only)
  useEffect(() => {
    if (!isOpen || !canManageMembers || !organization?.id) return;

    const unsubscribe = MemberService.subscribeToMembers(organization.id, (allMembers) => {
      dispatch(setMembers(allMembers));
    });

    return () => unsubscribe();
  }, [isOpen, canManageMembers, organization?.id, dispatch]);

  const handleClose = () => {
    dispatch(closeDialog());
  };

  const handleThemeToggle = () => {
    dispatch(setTheme(theme === 'light' ? 'dark' : 'light'));
  };

  const handleCopyCode = () => {
    if (organization?.inviteCode) {
      navigator.clipboard.writeText(organization.inviteCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleRegenerateCode = async () => {
    if (!organization?.id) return;
    const newCode = await AuthService.regenerateInviteCode(organization.id);
    dispatch(setOrganization({ ...organization, inviteCode: newCode }));
  };

  const handleApprove = async (member: OrgMember) => {
    if (!organization?.id) return;
    const role = approveRole[member.id] || 'viewer';
    await MemberService.approveMember(organization.id, member.id, role);
  };

  const handleReject = async (member: OrgMember) => {
    if (!organization?.id) return;
    await MemberService.rejectMember(organization.id, member.id);
  };

  const handleRoleChange = async (memberId: string, newRole: MemberRole) => {
    if (!organization?.id) return;
    // Don't allow admins to demote themselves
    if (memberId === user?.userID) return;
    await MemberService.changeRole(organization.id, memberId, newRole);
  };

  const handleJoinOrg = async () => {
    if (!joinCode || joinCode.length < 6) {
      setJoinError('Please enter a valid 6-character invite code.');
      return;
    }
    setJoinLoading(true);
    setJoinError(null);
    try {
      const org = await AuthService.joinOrganization(joinCode);
      dispatch(setOrganization(org));
      if (user) {
        dispatch(setUser({ ...user, organizationID: org.id, memberStatus: 'pending', role: 'viewer' }));
      }
      setJoinSuccess(true);
      setJoinCode('');
    } catch (err: any) {
      setJoinError(err.message || 'Failed to join organization.');
    } finally {
      setJoinLoading(false);
    }
  };

  const pendingMembers = members.filter((m) => m.memberStatus === 'pending');
  const activeMembers = members.filter((m) => m.memberStatus === 'active');

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Settings color="primary" />
          <Typography variant="h6">Settings</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 1 }}>
          {/* User Information */}
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Your Account
          </Typography>
          <List dense>
            <ListItem
              secondaryAction={
                editingName ? (
                  <Button size="small" onClick={handleNameSave} disabled={nameSaving}>
                    {nameSaving ? <CircularProgress size={16} /> : 'Done'}
                  </Button>
                ) : (
                  <IconButton size="small" onClick={() => setEditingName(true)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                )
              }
            >
              <ListItemIcon><Person /></ListItemIcon>
              {editingName ? (
                <Box sx={{ display: 'flex', gap: 1, mr: 6 }}>
                  <TextField
                    size="small"
                    label="First"
                    value={editFirstName}
                    onChange={(e) => setEditFirstName(e.target.value)}
                    autoFocus
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    size="small"
                    label="Last"
                    value={editLastName}
                    onChange={(e) => setEditLastName(e.target.value)}
                    sx={{ flex: 1 }}
                  />
                </Box>
              ) : (
                <ListItemText
                  primary={`${user?.firstName || 'User'} ${user?.lastName || ''}`}
                  secondary="Name"
                />
              )}
            </ListItem>
            <ListItem>
              <ListItemIcon><Person /></ListItemIcon>
              <ListItemText
                primary={user?.userEmail || 'Guest'}
                secondary="Email"
              />
              {user?.role && (
                <Chip
                  label={roleLabels[user.role]}
                  color={roleColors[user.role]}
                  size="small"
                />
              )}
            </ListItem>
            {organization && (
              <ListItem>
                <ListItemIcon><Badge /></ListItemIcon>
                <ListItemText
                  primary={organization.name}
                  secondary="Organization"
                />
              </ListItem>
            )}
          </List>

          <Divider sx={{ my: 2 }} />

          {/* Join Organization (available to all users) */}
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            <GroupAdd sx={{ verticalAlign: 'middle', mr: 1, fontSize: 20 }} />
            Join Organization
          </Typography>
          {joinSuccess ? (
            <Alert severity="info" sx={{ mb: 1 }}>
              Request sent. Waiting for admin approval.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, ml: 2, mb: 1 }}>
              <TextField
                size="small"
                label="Invite Code"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                inputProps={{ maxLength: 6, style: { letterSpacing: '0.15em', fontFamily: 'monospace', fontWeight: 700 } }}
                error={!!joinError}
                helperText={joinError || 'Enter a 6-character code to join another organization'}
                sx={{ flex: 1 }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={handleJoinOrg}
                disabled={joinLoading || joinCode.length < 6}
                sx={{ mt: 0.5 }}
              >
                {joinLoading ? <CircularProgress size={20} /> : 'Join'}
              </Button>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Admin: Invite Code */}
          {canManageMembers && organization && (
            <>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Invite Code
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, ml: 2 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: 'monospace',
                    letterSpacing: '0.15em',
                    fontWeight: 700,
                    bgcolor: 'action.hover',
                    px: 2,
                    py: 0.5,
                    borderRadius: 1,
                  }}
                >
                  {organization.inviteCode || 'N/A'}
                </Typography>
                <Tooltip title={copied ? 'Copied!' : 'Copy code'}>
                  <IconButton onClick={handleCopyCode} size="small">
                    {copied ? <Check color="success" /> : <ContentCopy />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Generate new code (invalidates old one)">
                  <IconButton onClick={handleRegenerateCode} size="small">
                    <Refresh />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                Share this code with people you want to invite. Regenerating creates a new code and invalidates the old one.
              </Typography>

              <Divider sx={{ my: 2 }} />
            </>
          )}

          {/* Admin: Pending Members */}
          {canManageMembers && pendingMembers.length > 0 && (
            <>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Pending Requests
                <Chip label={pendingMembers.length} size="small" color="warning" sx={{ ml: 1 }} />
              </Typography>
              <List dense>
                {pendingMembers.map((member) => (
                  <ListItem
                    key={member.id}
                    sx={{
                      bgcolor: 'action.hover',
                      borderRadius: 1,
                      mb: 0.5,
                    }}
                  >
                    <ListItemIcon><Person /></ListItemIcon>
                    <ListItemText
                      primary={member.displayName}
                      secondary={member.email}
                    />
                    <Select
                      size="small"
                      value={approveRole[member.id] || 'viewer'}
                      onChange={(e) =>
                        setApproveRole((prev) => ({
                          ...prev,
                          [member.id]: e.target.value as MemberRole,
                        }))
                      }
                      sx={{ mr: 1, minWidth: 100 }}
                    >
                      <MenuItem value="viewer">Viewer</MenuItem>
                      <MenuItem value="editor">Editor</MenuItem>
                    </Select>
                    <Tooltip title="Approve">
                      <IconButton
                        color="success"
                        onClick={() => handleApprove(member)}
                        size="small"
                      >
                        <Check />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Reject">
                      <IconButton
                        color="error"
                        onClick={() => handleReject(member)}
                        size="small"
                      >
                        <Close />
                      </IconButton>
                    </Tooltip>
                  </ListItem>
                ))}
              </List>

              <Divider sx={{ my: 2 }} />
            </>
          )}

          {/* Admin: Active Members */}
          {canManageMembers && activeMembers.length > 0 && (
            <>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                <Group sx={{ verticalAlign: 'middle', mr: 1, fontSize: 20 }} />
                Members
                <Chip label={activeMembers.length} size="small" sx={{ ml: 1 }} />
              </Typography>
              <List dense>
                {activeMembers.map((member) => {
                  const isSelf = member.id === user?.userID;
                  return (
                    <ListItem key={member.id}>
                      <ListItemIcon><Person /></ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {member.displayName}
                            {isSelf && (
                              <Chip label="You" size="small" variant="outlined" />
                            )}
                          </Box>
                        }
                        secondary={member.email}
                      />
                      {isSelf ? (
                        <Chip
                          label={roleLabels[member.role]}
                          color={roleColors[member.role]}
                          size="small"
                        />
                      ) : (
                        <Select
                          size="small"
                          value={member.role}
                          onChange={(e) =>
                            handleRoleChange(member.id, e.target.value as MemberRole)
                          }
                          sx={{ minWidth: 100 }}
                        >
                          <MenuItem value="viewer">Viewer</MenuItem>
                          <MenuItem value="editor">Editor</MenuItem>
                          <MenuItem value="admin">Admin</MenuItem>
                        </Select>
                      )}
                    </ListItem>
                  );
                })}
              </List>

              <Divider sx={{ my: 2 }} />
            </>
          )}

          {/* Appearance Settings */}
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Appearance
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon>
                {theme === 'dark' ? <Brightness7 /> : <Brightness4 />}
              </ListItemIcon>
              <ListItemText
                primary="Theme"
                secondary="Choose between light and dark mode"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={theme === 'dark'}
                    onChange={handleThemeToggle}
                  />
                }
                label={theme === 'dark' ? 'Dark' : 'Light'}
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          {/* App Information */}
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            About
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><Info /></ListItemIcon>
              <ListItemText
                primary="Check In Web App"
                secondary="Inventory management system for tracking check-in/check-out of items"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Apple /></ListItemIcon>
              <ListItemText
                primary="iOS App"
                secondary={
                  <Link
                    href="https://apps.apple.com/us/app/easy-check-in-check-out-system/id6443909129"
                    target="_blank"
                    rel="noopener noreferrer"
                    color="primary"
                    underline="hover"
                  >
                    Download on the App Store
                  </Link>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Email /></ListItemIcon>
              <ListItemText
                primary="We love feedback!"
                secondary={
                  <Link
                    href="mailto:info@codeprostudios.com?subject=Feedback from My Inventory Web App"
                    color="primary"
                    underline="hover"
                  >
                    info@codeprostudios.com
                  </Link>
                }
              />
            </ListItem>
          </List>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="contained">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SettingsDialog;
